/* ============================================================================
   Derivaciones. Funciones puras sobre el dataset: deltas, recortes de rango,
   asequibilidad, precio base de compra y ordenamientos.

   Regla: si un cálculo no se puede hacer con los datos disponibles, devuelve
   `null`. Nunca un cero, nunca un valor «aproximado» sin marca. La interfaz
   distingue «no hay dato» de «el dato es cero».
   ========================================================================== */

import { monthsBetween, periodIndex, type Period } from '../lib/format'
import { RETAIL_CHANNEL_LABEL } from './schema'
import type {
  Dataset,
  Place,
  PlaceKind,
  PlaceSeries,
  PlaceSeriesKind,
  PriceBand,
  PriceCell,
  Product,
  ProductSeries,
  ProductSeriesKind,
  RetailChainRanking,
  RetailComparator,
  RetailReference,
  Series,
  SeriesPoint,
  WholesaleDirectory,
} from './schema'

/* --- Recortes ------------------------------------------------------------ */

export type RangeKey = '12m' | '24m' | '60m' | 'all'

export const RANGE_OPTIONS: readonly { value: RangeKey; label: string; hint: string }[] = [
  { value: '12m', label: '1 año', hint: 'Últimos 12 meses' },
  { value: '24m', label: '2 años', hint: 'Últimos 24 meses' },
  { value: '60m', label: '5 años', hint: 'Últimos 60 meses' },
  { value: 'all', label: 'Todo', hint: 'Toda la serie disponible' },
]

const RANGE_MONTHS: Record<RangeKey, number | null> = {
  '12m': 12,
  '24m': 24,
  '60m': 60,
  all: null,
}

/** Recorta la serie al rango, tomando como ancla el último punto disponible. */
export function sliceRange(points: SeriesPoint[], range: RangeKey): SeriesPoint[] {
  const months = RANGE_MONTHS[range]
  if (months === null || points.length === 0) return points
  const last = points[points.length - 1]!
  const cutoff = periodIndex(last.period) - (months - 1)
  return points.filter((p) => periodIndex(p.period) >= cutoff)
}

/** Rangos que la serie realmente puede ofrecer. Nunca mostramos un rango vacío. */
export function availableRanges(points: SeriesPoint[]): RangeKey[] {
  if (points.length < 2) return ['all']
  const span = monthsBetween(points[0]!.period, points[points.length - 1]!.period) + 1
  const out: RangeKey[] = []
  if (span >= 12) out.push('12m')
  if (span >= 24) out.push('24m')
  if (span >= 60) out.push('60m')
  out.push('all')
  return out
}

/* --- Deltas -------------------------------------------------------------- */

export interface Change {
  from: SeriesPoint
  to: SeriesPoint
  absolute: number
  percent: number
  months: number
}

/** Cambio entre el último punto y el de `monthsBack` meses antes. */
export function changeOver(points: SeriesPoint[], monthsBack: number): Change | null {
  if (points.length < 2) return null
  const to = points[points.length - 1]!
  const targetIndex = periodIndex(to.period) - monthsBack
  const from = points.find((p) => periodIndex(p.period) === targetIndex)
  if (!from || from.value === 0) return null
  return {
    from,
    to,
    absolute: to.value - from.value,
    percent: (to.value / from.value - 1) * 100,
    months: monthsBack,
  }
}

/** Cambio entre dos periodos concretos. */
export function changeBetween(
  points: SeriesPoint[],
  fromPeriod: Period,
  toPeriod: Period,
): Change | null {
  const from = points.find((p) => p.period === fromPeriod)
  const to = points.find((p) => p.period === toPeriod)
  if (!from || !to || from.value === 0) return null
  return {
    from,
    to,
    absolute: to.value - from.value,
    percent: (to.value / from.value - 1) * 100,
    months: monthsBetween(fromPeriod, toPeriod),
  }
}

/** Cambio del primer al último punto disponible de la serie. */
export function changeSinceFirst(points: SeriesPoint[]): Change | null {
  if (points.length < 2) return null
  const from = points[0]!
  const to = points[points.length - 1]!
  if (from.value === 0) return null
  return {
    from,
    to,
    absolute: to.value - from.value,
    percent: (to.value / from.value - 1) * 100,
    months: monthsBetween(from.period, to.period),
  }
}

/** Valor en un periodo, o el último disponible si ese mes no existe. */
export function valueAtOrLast(points: SeriesPoint[], period: Period | null): SeriesPoint | null {
  if (points.length === 0) return null
  if (period === null) return points[points.length - 1]!
  const exact = points.find((p) => p.period === period)
  return exact ?? points[points.length - 1]!
}

/** Últimos n valores, para la sparkline. */
export function tail(points: SeriesPoint[], n = 12): number[] {
  return points.slice(-n).map((p) => p.value)
}

/* --- Variación por producto --------------------------------------------- */

export interface ProductChange {
  product: Product
  series: ProductSeries
  latest: SeriesPoint
  monthly: Change | null
  annual: Change | null
  /** Acumulado desde el primer mes de la serie. */
  sinceStart: Change | null
}

/** Horizonte de comparación disponible para las series de producto. */
export type Horizon = 'monthly' | 'annual' | 'sinceStart'

export function changeFor(row: ProductChange, horizon: Horizon): Change | null {
  if (horizon === 'monthly') return row.monthly
  if (horizon === 'annual') return row.annual
  return row.sinceStart
}

/**
 * Horizontes que los datos realmente soportan. La serie por producto arranca en
 * julio de 2025, así que hoy NO existe comparación contra el mismo mes del año
 * anterior: ofrecer «cambio anual» sin dato producía «0.0 % sin cambio» en los
 * 42 renglones, que es una mentira, no un vacío.
 */
export function availableHorizons(rows: ProductChange[]): Horizon[] {
  const out: Horizon[] = []
  if (rows.some((r) => r.monthly !== null)) out.push('monthly')
  if (rows.some((r) => r.annual !== null)) out.push('annual')
  if (rows.some((r) => r.sinceStart !== null && (r.annual === null || r.sinceStart.months > 12))) {
    out.push('sinceStart')
  }
  return out
}

export function productChanges(
  dataset: Dataset,
  geography: string,
  kind: ProductSeriesKind = 'precio',
): ProductChange[] {
  const byId = new Map(dataset.products.map((p) => [p.id, p]))
  const relevantes = dataset.productSeries.filter(
    (s) => s.geography === geography && s.kind === kind && s.points.length > 0,
  )

  /*
     El acumulado se mide desde un ORIGEN COMÚN: el mes más tardío entre los
     primeros de todas las series mostradas. Usar el primer punto de cada serie
     ordenaba juntos cambios de ventanas distintas —un renglón que entró tarde
     competía con doce meses de otro— bajo un solo rótulo de «N meses».
     Un renglón que no llegue a ese origen sale como null, o sea «sin dato».
  */
  const origenComun = relevantes
    .map((s) => s.points[0]!.period)
    .reduce<string | null>((acc, p) => (acc === null || p > acc ? p : acc), null)

  const out: ProductChange[] = []
  for (const series of relevantes) {
    const product = byId.get(series.productId)
    if (!product) continue
    const last = series.points[series.points.length - 1]!
    out.push({
      product,
      series,
      latest: last,
      monthly: changeOver(series.points, 1),
      annual: changeOver(series.points, 12),
      sinceStart:
        origenComun === null ? null : changeBetween(series.points, origenComun, last.period),
    })
  }
  return out
}

/** Series de producto de una geografía y medida, indexadas por producto. */
export function productSeriesIndex(
  dataset: Dataset,
  geography: string,
  kind: ProductSeriesKind,
): Map<string, ProductSeries> {
  const map = new Map<string, ProductSeries>()
  for (const series of dataset.productSeries) {
    if (series.geography === geography && series.kind === kind) {
      map.set(series.productId, series)
    }
  }
  return map
}

export type ProductSort = 'sube' | 'baja' | 'price-desc' | 'name'

export const PRODUCT_SORTS: readonly { value: ProductSort; label: string }[] = [
  { value: 'sube', label: 'Lo que más se encareció' },
  { value: 'baja', label: 'Lo que más se abarató' },
  { value: 'price-desc', label: 'Precio más alto' },
  { value: 'name', label: 'Nombre' },
]

/**
 * Ordena por el horizonte activo. Los renglones sin dato en ese horizonte van
 * SIEMPRE al final, en los dos sentidos: un vacío no es un mínimo ni un máximo.
 */
export function sortProductChanges(
  rows: ProductChange[],
  sort: ProductSort,
  horizon: Horizon,
): ProductChange[] {
  const copy = [...rows]
  if (sort === 'price-desc') return copy.sort((a, b) => b.latest.value - a.latest.value)
  if (sort === 'name') {
    return copy.sort((a, b) => a.product.name.localeCompare(b.product.name, 'es-MX'))
  }
  const sign = sort === 'sube' ? -1 : 1
  return copy.sort((a, b) => {
    const ca = changeFor(a, horizon)
    const cb = changeFor(b, horizon)
    if (ca === null && cb === null) return a.product.name.localeCompare(b.product.name, 'es-MX')
    if (ca === null) return 1
    if (cb === null) return -1
    return sign * (ca.percent - cb.percent)
  })
}

/* --- Asequibilidad ------------------------------------------------------- */

export interface Affordability {
  period: Period
  /** Salario mínimo mensual = diario × 30.4. */
  monthlyWage: number
  /** Costo de la canasta para un hogar promedio. */
  householdBasket: number
  /** Cuántas canastas de hogar compra un salario mínimo. */
  baskets: number
  /** Porcentaje del salario que se va en la canasta del hogar. */
  shareOfWage: number
  householdSize: number
}

/**
 * Mensualización del salario mínimo: 365/12 = 30.4167 días.
 *
 * Es el factor exacto de CONASAMI: 315.04 pesos diarios los reporta como
 * 9 582.47 mensuales, que es 315.04 × 365/12. Usar 30.4 redondo daba 9 577 y
 * dejaba la cifra de la app 5 pesos por debajo de la oficial, además de que dos
 * vistas mostraban salarios distintos. Un solo lugar para el factor.
 */
export const DAYS_PER_MONTH = 365 / 12

/**
 * Cruza la canasta por persona con el salario mínimo. Ambas series deben existir
 * en el mismo mes: no extrapolamos ninguna de las dos.
 */
export function affordabilitySeries(
  basketPerPerson: Series,
  dailyWage: Series,
  householdSize: number,
): Affordability[] {
  const wageByPeriod = new Map(dailyWage.points.map((p) => [p.period, p.value]))
  const out: Affordability[] = []
  for (const point of basketPerPerson.points) {
    const wage = wageByPeriod.get(point.period)
    if (wage === undefined) continue
    const monthlyWage = wage * DAYS_PER_MONTH
    const householdBasket = point.value * householdSize
    if (householdBasket <= 0) continue
    out.push({
      period: point.period,
      monthlyWage,
      householdBasket,
      baskets: monthlyWage / householdBasket,
      shareOfWage: (householdBasket / monthlyWage) * 100,
      householdSize,
    })
  }
  return out
}

/* --- Utilidades ---------------------------------------------------------- */

/** Serie con puntos recortados. Útil para pasarla a la gráfica. */
export function withPoints(series: Series, points: SeriesPoint[]): Series {
  return { ...series, points }
}

/** Geografías presentes en las series de producto. */
export function productGeographies(dataset: Dataset): string[] {
  return Array.from(new Set(dataset.productSeries.map((s) => s.geography))).sort((a, b) =>
    a.localeCompare(b, 'es-MX'),
  )
}

/** ¿Hay suficiente para armar una vista? */
export function hasProductData(dataset: Dataset): boolean {
  return dataset.productSeries.some((s) => s.points.length >= 2)
}

/* --- Despensa ------------------------------------------------------------ */

export interface DespensaLine {
  product: Product
  /** Cantidad implícita para el hogar. `null` si no hay precio con qué dividir. */
  quantity: number | null
  unit: string
  /** Precio unitario publicado. `null` si la fuente no lo trae ese mes. */
  unitPrice: number | null
  /** Costo de este renglón para el hogar en el periodo. */
  cost: number
  /** Porcentaje del costo total. */
  share: number
  /** El precio de este renglón no es por peso y no es comparable. */
  nonComparableUnit: boolean
}

export interface Despensa {
  period: Period
  geography: string
  people: number
  days: number
  lines: DespensaLine[]
  total: number
  /** Valor oficial de la canasta escalado al hogar y al periodo, para contrastar. */
  officialTotal: number | null
}

/** Días con que el INEGI mensualiza la canasta. Mantener alineado con el pipeline. */
export const CANASTA_DAYS_PER_MONTH = 30

/**
 * Arma la despensa a partir del desglose oficial: por cada renglón, el valor
 * mensual por persona escalado al tamaño del hogar y al número de días.
 *
 * Los costos salen de la serie `valor-mensual` (lo que la fuente publica), no de
 * multiplicar gramos por precio: así el total de la despensa reproduce el valor
 * oficial de la canasta en vez de aproximarlo.
 */
export function buildDespensa(
  dataset: Dataset,
  geography: string,
  people: number,
  days: number,
  period: Period | null,
  basketSeries: Series | null,
): Despensa {
  const factor = (people * days) / CANASTA_DAYS_PER_MONTH
  const valueIndex = productSeriesIndex(dataset, geography, 'valor-mensual')
  const priceIndex = productSeriesIndex(dataset, geography, 'precio')
  const byId = new Map(dataset.products.map((p) => [p.id, p]))

  /*
     El periodo se resuelve UNA vez y luego se exige exacto.
     Antes cada renglón usaba su propio último punto disponible: si a un producto
     le faltaba el mes más reciente, la despensa mezclaba precios de meses
     distintos y el total no correspondía a ningún mes real. Ahora un renglón sin
     dato en el periodo elegido simplemente no entra, y el contraste contra el
     valor oficial de la canasta lo delata.
  */
  const resolvedPeriod =
    period ??
    [...valueIndex.values()]
      .map((s) => s.points[s.points.length - 1]?.period)
      .filter((p): p is Period => p !== undefined)
      .sort()
      .pop() ??
    null

  const lines: DespensaLine[] = []
  let total = 0

  for (const [productId, series] of valueIndex) {
    const product = byId.get(productId)
    if (!product) continue
    const point = series.points.find((p) => p.period === resolvedPeriod)
    if (!point) continue

    const pricePoint = priceIndex.get(productId)?.points.find((p) => p.period === resolvedPeriod)
    const cost = point.value * factor
    /* Sin precio publicado NO hay cero: hay ausencia. Un cero aquí imprimía
       «0.00 kg» y «$0.00 / kg» en la lista de compra, que es una afirmación
       falsa; el costo del renglón sí es válido y se conserva. */
    const unitPrice = pricePoint?.value ?? null
    const nonComparable = product.unit === 'ración'
    const quantity =
      unitPrice !== null && unitPrice > 0 && !nonComparable ? cost / unitPrice : null

    total += cost
    lines.push({
      product,
      quantity,
      unit: product.unit,
      unitPrice,
      cost,
      share: 0,
      nonComparableUnit: nonComparable,
    })
  }

  for (const line of lines) line.share = total > 0 ? (line.cost / total) * 100 : 0
  lines.sort((a, b) => b.cost - a.cost)

  const officialPoint = basketSeries
    ? (basketSeries.points.find((p) => p.period === resolvedPeriod) ?? null)
    : null

  return {
    period: resolvedPeriod ?? '',
    geography,
    people,
    days,
    lines,
    total,
    officialTotal: officialPoint ? officialPoint.value * factor : null,
  }
}

export function seriesByBasket(dataset: Dataset, basketId: string): Series[] {
  return dataset.series.filter((s) => s.basketId === basketId)
}

/* --- Territorio ----------------------------------------------------------
   El INPC por entidad y por ciudad responde una pregunta que la canasta
   nacional no puede: ¿dónde se está encareciendo más la comida?

   Todo lo que sigue trabaja en CAMBIOS, nunca en niveles. Comparar el índice de
   dos lugares no dice nada —cada uno está anclado a su propio julio de 2018—,
   así que aquí no existe ninguna función que devuelva «el índice de un lugar»
   para ponerlo junto al de otro.
   ------------------------------------------------------------------------ */

/** Ventana común: la primera fecha en que las 88 series existen a la vez. */
export const TERRITORY_BASE_PERIOD = '2018-07'

export interface PlaceChange {
  place: Place
  points: SeriesPoint[]
  /** Variación en los últimos 12 meses. */
  annual: Change | null
  /** Variación acumulada desde la base común. */
  sinceBase: Change | null
  /**
   * Diferencia en puntos porcentuales contra el nacional en la variación anual.
   * Positivo: ahí los alimentos subieron más que en el país.
   */
  gapVsNational: number | null
}

/** Índice de series territoriales por lugar, para una medida. */
export function placeSeriesIndex(
  dataset: Dataset,
  kind: PlaceSeriesKind,
): Map<string, PlaceSeries> {
  const out = new Map<string, PlaceSeries>()
  for (const s of dataset.placeSeries) if (s.kind === kind) out.set(s.placeId, s)
  return out
}

export function findPlace(dataset: Dataset, id: string): Place | null {
  return dataset.places.find((p) => p.id === id) ?? null
}

export function nationalPlace(dataset: Dataset): Place | null {
  return dataset.places.find((p) => p.kind === 'nacional') ?? null
}

/**
 * Cambios por lugar para un tipo de lugar.
 *
 * `gapVsNational` sólo se calcula cuando el lugar y el nacional terminan en el
 * MISMO mes. Restar la variación anual de un lugar actualizado a mayo contra un
 * nacional de junio produciría una brecha inventada, y es exactamente el error
 * que ya se coló antes al dividir canasta entre salario de meses distintos.
 */
export function placeChanges(
  dataset: Dataset,
  kind: PlaceKind,
  seriesKind: PlaceSeriesKind = 'alimentos',
): PlaceChange[] {
  const index = placeSeriesIndex(dataset, seriesKind)
  const national = nationalPlace(dataset)
  const nationalSeries = national ? (index.get(national.id) ?? null) : null
  const nationalAnnual = nationalSeries ? changeOver(nationalSeries.points, 12) : null

  const out: PlaceChange[] = []
  for (const place of dataset.places) {
    if (place.kind !== kind) continue
    const series = index.get(place.id)
    if (!series || series.points.length === 0) continue

    const annual = changeOver(series.points, 12)
    const sinceBase = changeBetween(
      series.points,
      TERRITORY_BASE_PERIOD,
      series.points[series.points.length - 1]!.period,
    )
    const sameMonth =
      annual !== null && nationalAnnual !== null && annual.to.period === nationalAnnual.to.period

    out.push({
      place,
      points: series.points,
      annual,
      sinceBase,
      gapVsNational: sameMonth ? annual.percent - nationalAnnual.percent : null,
    })
  }
  return out
}

export type PlaceSort = 'sube' | 'baja' | 'name'

/**
 * Qué variación se ordena y se dibuja: la del último año o la acumulada desde la
 * base común. Es una elección del lector, no un criterio de orden: por eso vive
 * aparte de `PlaceSort`, que sólo dice en qué dirección.
 */
export type PlaceHorizon = 'anual' | 'acumulada'

/**
 * Rótulos neutros respecto del signo. «Donde menos sube» era falso en el mes en
 * que el extremo bajo es negativo —hoy Tlaxcala, con −1.4 %—: ahí no sube menos,
 * baja. Las tarjetas ya cambian su rótulo según el signo; este menú no puede
 * hacerlo porque nombra un criterio de orden, no un dato, así que se redacta de
 * forma que valga en los dos casos.
 */
export const PLACE_SORTS: readonly { value: PlaceSort; label: string }[] = [
  { value: 'sube', label: 'De mayor a menor variación' },
  { value: 'baja', label: 'De menor a mayor variación' },
  { value: 'name', label: 'Alfabético' },
]

export function placeMetric(row: PlaceChange, horizon: PlaceHorizon): Change | null {
  return horizon === 'acumulada' ? row.sinceBase : row.annual
}

/**
 * Ordena por la variación del horizonte activo, dejando SIEMPRE al final los
 * lugares sin dato.
 *
 * El horizonte y el orden son dos ejes distintos y tienen que combinarse: «donde
 * menos sube» con horizonte acumulado significa el acumulado más chico, no el
 * anual. Antes el horizonte acumulado forzaba el criterio y se tragaba en
 * silencio la dirección que el lector había elegido: el menú seguía diciendo
 * «Donde menos sube» y la tabla mostraba lo contrario.
 */
export function sortPlaceChanges(
  rows: PlaceChange[],
  sort: PlaceSort,
  horizon: PlaceHorizon,
): PlaceChange[] {
  const collator = new Intl.Collator('es-MX', { sensitivity: 'base' })

  return [...rows].sort((a, b) => {
    const ka = placeMetric(a, horizon)?.percent ?? null
    const kb = placeMetric(b, horizon)?.percent ?? null
    /* Los lugares sin dato van al final SIEMPRE, también en orden alfabético: la
       vista promete que se listan aparte, y con la A de un lugar sin barra
       encabezando la tabla esa promesa era falsa. El nombre ordena dentro de
       cada grupo, no entre grupos. */
    if (ka === null && kb !== null) return 1
    if (ka !== null && kb === null) return -1
    if (sort === 'name' || ka === null || kb === null) {
      return collator.compare(a.place.name, b.place.name)
    }
    if (ka === kb) return collator.compare(a.place.name, b.place.name)
    return sort === 'baja' ? ka - kb : kb - ka
  })
}

export interface TerritorySummary {
  /** Variación nacional en el horizonte activo: la referencia de todo lo demás. */
  national: Change | null
  nationalPlace: Place | null
  /** Lugar con la variación más alta del horizonte activo. */
  highest: PlaceChange | null
  /** Lugar con la variación más baja. Puede ser negativa: entonces bajó. */
  lowest: PlaceChange | null
  /** Distancia en puntos porcentuales entre los extremos. */
  spread: number | null
  /** Cuántos lugares quedaron por encima del nacional. */
  aboveNational: number
  /** Mes al que corresponde la comparación. Vacío si no hay dato. */
  period: Period
  /** Lugares que entraron en el resumen. Es el denominador de `aboveNational`. */
  total: number
  /** Lugares excluidos por no cerrar en el mismo mes que el nacional. */
  excluded: number
}

/**
 * Resumen del horizonte activo.
 *
 * Dos cosas que parecen detalle y no lo son:
 *
 * 1. Sigue al horizonte. Si el lector pidió «Desde 2018», las tarjetas tienen que
 *    hablar del acumulado; dejarlas en la variación anual mientras la tabla de
 *    abajo muestra la acumulada produce dos respuestas distintas a la misma
 *    pregunta en la misma pantalla.
 * 2. Sólo entran los lugares que cierran en el MISMO mes que el nacional. Un
 *    lugar rezagado traería una ventana desplazada, y ese dato entraría al
 *    ranking, al extremo y a la brecha sin que nada lo dijera. Los excluidos se
 *    cuentan aparte para que la vista pueda declararlos en vez de callarlos.
 */
export function territorySummary(
  rows: PlaceChange[],
  dataset: Dataset,
  horizon: PlaceHorizon = 'anual',
): TerritorySummary {
  const national = nationalPlace(dataset)
  const nationalSeries = national
    ? (placeSeriesIndex(dataset, 'alimentos').get(national.id) ?? null)
    : null

  const nationalChange = !nationalSeries
    ? null
    : horizon === 'acumulada'
      ? changeBetween(
          nationalSeries.points,
          TERRITORY_BASE_PERIOD,
          nationalSeries.points[nationalSeries.points.length - 1]?.period ?? '',
        )
      : changeOver(nationalSeries.points, 12)

  const referencia = nationalChange?.to.period ?? null
  const conDato = rows.filter((r) => placeMetric(r, horizon) !== null)
  const comparables =
    referencia === null
      ? conDato
      : conDato.filter((r) => placeMetric(r, horizon)!.to.period === referencia)

  const sorted = [...comparables].sort(
    (a, b) => placeMetric(a, horizon)!.percent - placeMetric(b, horizon)!.percent,
  )
  const lowest = sorted[0] ?? null
  const highest = sorted[sorted.length - 1] ?? null

  const nacionalPct = nationalChange?.percent ?? null

  return {
    national: nationalChange,
    nationalPlace: national,
    highest,
    lowest,
    spread:
      highest && lowest
        ? placeMetric(highest, horizon)!.percent - placeMetric(lowest, horizon)!.percent
        : null,
    aboveNational:
      nacionalPct === null
        ? 0
        : comparables.filter((r) => placeMetric(r, horizon)!.percent > nacionalPct).length,
    period: referencia ?? (comparables[0] ? placeMetric(comparables[0], horizon)!.to.period : ''),
    total: comparables.length,
    excluded: conDato.length - comparables.length,
  }
}

/**
 * Reexpresa una serie de índice en 100 = `basePeriod`.
 *
 * Hace falta para graficar dos lugares juntos. Las series del INPC están todas
 * en base segunda quincena de julio de 2018, pero su valor MENSUAL de julio de
 * 2018 no es idéntico: las entidades y las ciudades incorporadas con esa base
 * valen 100 clavado, y las ciudades de la muestra antigua, empalmadas desde
 * 1970 o 1982, valen alrededor de 99.7 porque el mes promedia dos quincenas.
 * Dibujarlas crudas metería una separación de origen que se leería como
 * diferencia de precios. Rebasadas, las dos curvas arrancan del mismo punto y
 * lo que separa a una de otra es lo único que significa algo: cuánto subió cada
 * una desde esa fecha.
 *
 * Los puntos NO se marcan como `derived`. En esta app esa marca significa «este
 * valor no se observó, se estimó», y la gráfica lo dibuja punteado con la nota
 * «el tramo punteado es estimado, no observado». Un índice rebaseado sí se
 * observó: lo único que cambió es la unidad en que se expresa, igual que pasar
 * de pesos a miles de pesos. Marcarlo como estimado sería decirle al lector que
 * la cifra es inventada. La reexpresión se declara en el subtítulo y en el pie
 * de la gráfica, que es donde corresponde.
 */
export function rebased(points: SeriesPoint[], basePeriod: Period): SeriesPoint[] {
  const base = points.find((p) => p.period === basePeriod)
  if (!base || base.value === 0) return points
  return points.map((p) => ({ ...p, value: (p.value / base.value) * 100 }))
}

/**
 * Cuenta TODOS los puntos del dataset, recorriendo los tres arreglos de series.
 *
 * Vive aquí y no en la vista porque ya se quedó corta una vez: cuando el esquema
 * 2 agregó el desglose territorial, la tarjeta «Puntos de dato» de Fuentes siguió
 * sumando sólo `series` y `productSeries` y anunciaba 3 726 puntos en un archivo
 * de 12 174 —omitía el 69 %—, mientras el panel de estado, dos clics más abajo,
 * reportaba 8 448 puntos de una sola fuente. Justo en la pantalla cuyo trabajo es
 * hacer auditable todo lo demás. Con una función única, la próxima dimensión que
 * se agregue entra sola.
 */
export function countAllPoints(dataset: Dataset): number {
  const sum = (series: { points: SeriesPoint[] }[]): number =>
    series.reduce((acc, s) => acc + s.points.length, 0)
  return sum(dataset.series) + sum(dataset.productSeries) + sum(dataset.placeSeries)
}

/* --- Abasto mayorista ----------------------------------------------------
   Todo lo que sigue son conteos de establecimientos, no de capacidad. El DENUE
   no publica ventas ni volumen, así que ninguna de estas funciones puede
   responder «¿quién me puede surtir diez toneladas?»: responden dónde buscar.
   ------------------------------------------------------------------------ */

/** Clave que representa «todas las clases» en los filtros. */
export const ALL_CLASSES = 'todas'

export interface WholesaleRow {
  id: string
  name: string
  sublabel?: string
  total: number
  volume: number
}

/** Entidades ordenadas por número de mayoristas de la clase elegida. */
export function wholesaleByState(
  directory: WholesaleDirectory,
  classCode: string = ALL_CLASSES,
): WholesaleRow[] {
  const rows = directory.states.map((s) => ({
    id: s.stateCode,
    name: s.stateName,
    total: classCode === ALL_CLASSES ? s.total : (s.byClass[classCode] ?? 0),
    /* El conteo «de volumen» sólo existe agregado, no por clase: el DENUE lo
       cruza en el pipeline y guardarlo por clase multiplicaría el bloque por
       doce sin que ninguna vista lo pida. Con una clase filtrada se omite en vez
       de mostrar el total de la entidad, que sería una cifra de otra pregunta. */
    volume: classCode === ALL_CLASSES ? s.volume : 0,
  }))
  return rows.filter((r) => r.total > 0).sort((a, b) => b.total - a.total)
}

/** Plazas de abasto ordenadas por número de mayoristas de la clase elegida. */
export function wholesaleByHub(
  directory: WholesaleDirectory,
  classCode: string = ALL_CLASSES,
): WholesaleRow[] {
  const rows = directory.hubs.map((h) => ({
    id: h.id,
    name: h.municipality,
    sublabel: h.stateName,
    total: classCode === ALL_CLASSES ? h.total : (h.byClass[classCode] ?? 0),
    volume: classCode === ALL_CLASSES ? h.volume : 0,
  }))
  return rows.filter((r) => r.total > 0).sort((a, b) => b.total - a.total)
}

/** Total de la clase elegida en todo el país. */
export function wholesaleTotal(directory: WholesaleDirectory, classCode: string): number {
  if (classCode === ALL_CLASSES) return directory.total
  return directory.states.reduce((a, s) => a + (s.byClass[classCode] ?? 0), 0)
}

/* --- Precio base para compra (Profeco QQP) -------------------------------
   Todo lo que sigue lee `dataset.retail` y no calcula un solo margen. Dos
   límites que no son evidentes y que ya costaron caro en otras partes del
   archivo:

   1. El diferencial central↔anaquel lo produce el pipeline PAREADO por entidad
      y con intervalo de bootstrap (R8). Rehacerlo aquí con las dos medianas
      nacionales lo sesga hasta 57.7 puntos porcentuales —queso oaxaca granel:
      +96.2 % pareado contra +153.9 % sin parear—, y el sesgo mediano de no
      parear es de 4.0 pp. Por eso `desviacionGeografica` se niega a cruzar
      canales y lo único que esta capa hace con `anaquel` es colocar una marca
      sobre la banda, cuando el veredicto del pipeline ya trae número.

   2. Ninguna de estas funciones cruza dos periodos. Las bandas van en mayo de
      2026 y el boletín del INEGI que alimenta el resto del sitio va en junio:
      `valueAtOrLast` (arriba) tomaría el punto de junio y lo dibujaría sobre
      una banda de mayo sin decir nada. La tortilla lo enseña sola: $23.95 el
      kilo en el boletín de mayo y $24.00 en el de junio, y la marca se rotula
      con el mes de la banda.
   ------------------------------------------------------------------------ */

/**
 * La banda de (referencia, geografía, mes), o `null`.
 *
 * Coincidencia EXACTA en los tres ejes, sin respaldo al último mes disponible.
 * `retail.bands` sólo trae el último mes y `geography: 'MX'` —el resto vive en
 * `public/precio-base-2026.json`, que la vista carga aparte—, así que un
 * respaldo silencioso serviría la banda nacional de mayo bajo el rótulo de la
 * entidad y el mes que el lector eligió en la URL.
 */
export function retailBand(
  retail: RetailComparator | null,
  refId: string,
  geo: string,
  period: Period,
): PriceBand | null {
  if (retail === null) return null
  return (
    retail.bands.find(
      (b) => b.refId === refId && b.geography === geo && b.period === period,
    ) ?? null
  )
}

/* --- Califique una oferta (§4.5) ----------------------------------------- */

export type OfferTier = 'excepcional' | 'buena' | 'mercado' | 'cara' | 'fuera-de-mercado'

/**
 * Los cinco tramos, en el orden en que se evalúan.
 *
 * La prosa vive aquí y no en el JSX por la razón de siempre: es texto que
 * describe un umbral, y si el umbral se mueve el texto tiene que moverse con
 * él. `bound` nombra el cuantil que cierra cada tramo; el último no tiene
 * techo y por eso va `null`.
 */
export const OFFER_TIERS: readonly {
  tier: OfferTier
  bound: 'p10' | 'p25' | 'p75' | 'p90' | null
  title: string
  text: string
}[] = [
  {
    tier: 'excepcional',
    bound: 'p10',
    title: 'Excepcional',
    text:
      'Por debajo del 10 % más barato observado. Verifique calidad y volumen antes ' +
      'de firmar: a ese precio la central vende grado mayoreo de segunda.',
  },
  {
    tier: 'buena',
    bound: 'p25',
    title: 'Buena oferta',
    text: 'Por debajo del 25 % más barato observado.',
  },
  {
    tier: 'mercado',
    bound: 'p75',
    title: 'Precio de mercado',
    text: 'Dentro del rango en que se negocia normalmente.',
  },
  {
    tier: 'cara',
    bound: 'p90',
    title: 'Cara',
    text: 'Por arriba del 75 % de lo observado.',
  },
  {
    tier: 'fuera-de-mercado',
    bound: null,
    title: 'Fuera de mercado',
    text: 'Por arriba del 90 % de lo observado. Pida una segunda cotización.',
  },
]

/** Va siempre debajo del veredicto. Es el límite de la calificación, no un pie. */
export const OFERTA_LIMITES =
  'Esta calificación compara contra precios de venta levantados por Profeco, no contra ' +
  'cotizaciones de proveedor. No incluye flete, maniobras, empaque ni condiciones de pago.'

/** Lo que se dice en lugar del evaluador cuando la celda no pasa la compuerta R4. */
export const OFERTA_SIN_BASE =
  'No hay precio base publicable para este renglón; calificar una oferta contra una ' +
  'mediana que no representa a nadie sería peor que no calificarla.'

/**
 * Kilos por tonelada. Se puede multiplicar sin pensar porque las 45 referencias
 * en granel —las únicas que se licitan por tonelada— están TODAS en `kg`; las 9
 * que van en litros son las 9 de empaque en ese denominador. Este factor nunca
 * tiene que convertir litros a kilos, que sería una densidad inventada y R11 lo
 * prohíbe (el aceite pesa 0.92 kg/L, no 1).
 *
 * Se cumple porque `OFERTA_BULTO` lo aplica sólo a `kg`; hasta agosto de 2026
 * `scoreOffer` lo multiplicaba sin mirar el denominador y la vista lo invocaba
 * igual para las nueve referencias en litros, o sea que trataba una tonelada de
 * aceite como 1 000 litros cuando son 1 087.
 */
export const KG_POR_TONELADA = 1000

/**
 * En qué unidad pide el evaluador el bulto de la compra, y por cuánto hay que
 * multiplicar el delta unitario para llegar al total.
 *
 * Con `kg` el bulto va en toneladas y el factor son los 1 000 kilos de una
 * tonelada, que es una equivalencia y no un supuesto.
 *
 * Con `L` el bulto va en LITROS y el factor es 1. La alternativa —seguir
 * pidiendo toneladas y convertirlas— exige la densidad del producto, que es
 * exactamente el tipo de factor que R11 prohíbe meter a mano: con la densidad
 * fingida de 1 kg/L, 10 toneladas de aceite salen 10 000 litros en vez de
 * 10 870 y el ahorro que el comprador mete al acta se queda corto 8 %. Y no se
 * arregla trayendo la densidad de una tabla: son 0.92 kg/L el aceite, ~1.03 la
 * leche y ~1.04 el refresco, ninguna publicada por la fuente. Se le pide el
 * volumen a quien sí lo sabe —el comprador tiene la cotización enfrente— en vez
 * de inventárselo.
 */
export const OFERTA_BULTO = {
  kg: { singular: 'tonelada', plural: 'toneladas', unidadesPorBulto: KG_POR_TONELADA },
  L: { singular: 'litro', plural: 'litros', unidadesPorBulto: 1 },
} as const

/** Lo que la vista dice cuando pide litros en vez de toneladas. Va debajo de
 *  los campos, porque el comprador de aceite piensa en toneladas y aquí se le
 *  pide otra cosa. Sin cifras: la densidad que falta no se enseña, se declara
 *  ausente. */
export const OFERTA_BULTO_LITROS =
  'El volumen va en litros porque el precio base va por litro. Convertirlo desde toneladas ' +
  'exigiría la densidad del producto, que la fuente no publica y esta pantalla no inventa.'

export interface OfferScore {
  tier: OfferTier
  /** Rótulo del veredicto: «Buena oferta». */
  title: string
  /** La prosa del tramo. */
  text: string
  /** oferta − p50, por unidad del denominador. Negativo = ahorro. */
  unitDelta: number
  /** Bulto declarado por el comprador, tal cual, en la unidad que `OFERTA_BULTO`
   *  asigna al denominador: toneladas con `kg`, litros con `L`. `null` si no
   *  declaró. */
  bulk: number | null
  /** `unitDelta × bulto × unidadesPorBulto`. `null` sin bulto declarado —no se
   *  supone uno— y `null` también cuando el producto desborda: ver `scoreOffer`. */
  totalDelta: number | null
}

/**
 * Califica una oferta contra los cinco cuantiles de la celda (§4.5).
 *
 * `cell` es nullable a propósito: la vista pasa
 * `band.grade === 'publicable' ? band.central : null`, y con `solo-rango`
 * recibe `null` y pinta `OFERTA_SIN_BASE`. Así el evaluador no se puede
 * habilitar por olvido sobre una mediana que no representa a nadie —son 15 de
 * las 60 referencias, no un caso raro.
 *
 * La oferta tiene que ser un número finito y positivo. Un `0` —que es el estado
 * inicial de un `input` vacío— cae por debajo del p10 de cualquier referencia y
 * saldría coronado como «Excepcional».
 *
 * El bulto se declara o no se declara; sin él, `totalDelta` es `null` y la
 * vista sólo puede hablar de pesos por unidad. Suponer una tonelada sería
 * inventar la única cifra que el comprador mete al acta.
 *
 * `denominador` NO es opcional a propósito: es lo que decide si el bulto son
 * toneladas o litros (`OFERTA_BULTO`). Ponerle un valor por omisión volvería a
 * dejar pasar las nueve referencias en litros por la rama de los kilos, que es
 * de donde salió la densidad fingida de 1 kg/L que R11 prohíbe.
 */
export function scoreOffer(
  cell: PriceCell | null,
  oferta: number,
  bulto: number | null,
  denominador: RetailReference['denominador'],
): OfferScore | null {
  if (cell === null) return null
  if (!Number.isFinite(oferta) || oferta <= 0) return null

  const bulk = bulto !== null && Number.isFinite(bulto) && bulto > 0 ? bulto : null
  const unitDelta = oferta - cell.p50
  const total = bulk === null ? null : unitDelta * bulk * OFERTA_BULTO[denominador].unidadesPorBulto

  for (const tramo of OFFER_TIERS) {
    if (tramo.bound !== null && oferta > cell[tramo.bound]) continue
    return {
      tier: tramo.tier,
      title: tramo.title,
      text: tramo.text,
      unitDelta,
      bulk,
      /* El producto se comprueba DESPUÉS de multiplicar: dos entradas finitas
         pueden desbordar a Infinity (oferta 1e308 × 1e10 toneladas), y la vista
         formateaba ese Infinity como moneda e imprimía «cuestan $∞ más». En
         esta pantalla la ausencia se declara; `null` es lo que la vista ya sabe
         tratar y `Infinity` no es un número que quepa en un acta. */
      totalDelta: total !== null && Number.isFinite(total) ? total : null,
    }
  }
  /* Inalcanzable: el último tramo no tiene techo. Existe porque `for` sin
     `return` garantizado no le prueba nada al compilador. */
  return null
}

/* --- Vigencia sugerida (§4.4) -------------------------------------------- */

export interface SerieEstable {
  /** El valor que la serie repitió, en la unidad del precio. */
  valor: number
  /** Meses de la serie que lo repitieron. */
  meses: number
  desde: Period
  hasta: Period
}

/**
 * La serie mensual que no se movió un solo centavo, o `null`.
 *
 * Existe porque el R² de una serie plana es `0/0`. La recta de R12 se ajusta
 * sobre `ln(p50)`: sin varianza total no hay nada que explicar y el pipeline
 * emite `derivaMensualPct: 0, derivaR2: 0`, o sea que **el ajuste perfecto
 * llega con la misma cifra que el peor ajuste posible**. Con eso, la compuerta
 * de R² manda la serie más estable del dataset al texto que R12 reserva para
 * «no hay tendencia»: el azúcar a granel vale $22.00 los cinco meses y la
 * pantalla le ponía la advertencia más severa y la vigencia más corta, mientras
 * la papa (+20.6 %/mes) recibía una fecha. Son cuatro referencias —azúcar
 * $22.00, tortilla $26.00, frijol peruano $34.00 y chile macho $30.00, las
 * cinco observaciones mensuales idénticas— y ninguna es un caso de borde: son
 * los renglones de tonelada.
 *
 * La condición es conjuntiva a propósito. Una pendiente de cero **sin** serie
 * plana —una serie que sube y baja y vuelve al mismo lugar— también da R² 0, y
 * ahí el veredicto correcto sí es «sin tendencia clara»: no es estabilidad, es
 * ruido que se cancela. Por eso no basta con leer `deriva === 0` y hay que
 * mirar los puntos.
 */
export function serieEstable(
  deriva: number | null,
  points: readonly SeriesPoint[],
): SerieEstable | null {
  if (deriva !== 0) return null
  const primero = points[0]
  const ultimo = points[points.length - 1]
  if (primero === undefined || ultimo === undefined) return null
  if (points.length < 2) return null
  if (!points.every((p) => p.value === primero.value)) return null
  return {
    valor: primero.value,
    meses: points.length,
    desde: primero.period,
    hasta: ultimo.period,
  }
}

/** Cuánto se puede desfasar el precio base antes de convocar otra vez. */
export const VIGENCIA_DESFASE = 0.05

/**
 * Tope duro, en días.
 *
 * Con cinco meses de historia no se distingue una tendencia de la subida
 * estacional de marzo-mayo: la papa sube 20.6 %/mes con R² 0.95 y puede ser
 * pura estacionalidad. Extrapolar medio año a partir de eso sería fabricar una
 * fecha; 90 días es hasta donde la serie alcanza a responder.
 */
export const VIGENCIA_TOPE_DIAS = 90

/** R² por debajo del cual la deriva NO se presenta como tendencia (R12). */
export const DERIVA_R2_MINIMO = 0.5

export interface Vigencia {
  /** Días hasta el desfase, ya acotados a `VIGENCIA_TOPE_DIAS`. */
  dias: number
  /** Los mismos días SIN acotar, para poder decir «se desfasa 5 % en 5 meses».
   *  `null` cuando la deriva es exactamente cero y nunca llega al 5 %. */
  diasSinTope: number | null
  /** `true` si el tope recortó la cifra. La vista lo declara, no lo esconde. */
  acotado: boolean
  /** Fecha ISO `YYYY-MM-DD` = `desde` + `dias`. Puede caer en el pasado. */
  hasta: string
}

/**
 * Días hasta que la deriva acumulada supere el 5 %, acotados a 90.
 *
 * `null` cuando R² < 0.5: ahí la vista dice «sin tendencia clara — revise cada
 * mes» en vez de una fecha. Son 33 de las 60 referencias —35 con R² bajo y 2
 * sin serie suficiente, menos las 4 planas que ahora salen por arriba—, así que
 * la rama sin tendencia sigue siendo la mayoritaria y no el caso de borde.
 *
 * `desde` es **la última observación de la fuente**, no el día en que se abre la
 * pantalla. La recta se estimó sobre observaciones que terminan el 2026-05-29 y
 * contar los días desde hoy regalaba tantos días de validez como rezago traiga
 * la fuente —65 el 2026-08-02—: la papa (+20.6 %/mes, R² 0.95) imprimía
 * «revisar antes del 10 de agosto» cuando su ventana de 8 días se agotó el
 * 2026-06-06 y la deriva ya llevaba +49 % acumulado. Diez de las 23 bandas con
 * fecha tenían la ventana entera consumida y las diez la presentaban por
 * delante. Cuando `hasta` queda atrás de hoy, la vista tiene que decirlo: una
 * fecha vencida dice la verdad, una futura no. La fecha de emisión de la ficha
 * es otra cosa y se imprime aparte (§4.9).
 *
 * Comprobado con las dos puntas del dataset: papa `a102-granel-alfa`
 * (+20.6 %/mes, R² 0.949) da 7.92 días → 8, hasta 2026-06-06; frijol
 * `a137-granel-negro` (−0.97 %/mes, R² 0.78) da 160 días → 90, `acotado`, hasta
 * 2026-08-27.
 *
 * `plana` sale de `serieEstable` y NO tiene valor por omisión, por la misma
 * razón que `denominador` en `scoreOffer`: un valor por omisión reinstala en
 * silencio el defecto que este parámetro existe para cerrar. La serie plana se
 * resuelve **antes** de la compuerta de R², porque el 0.00 que trae el dataset
 * en esas cuatro referencias es `0/0` y no una medida de ajuste.
 */
export function vigenciaHasta(
  deriva: number | null,
  r2: number | null,
  desde: string,
  plana: boolean,
): Vigencia | null {
  if (deriva === null || r2 === null) return null
  if (!Number.isFinite(deriva) || !Number.isFinite(r2)) return null

  /* Pendiente cero, antes de la compuerta. Con la serie plana es estabilidad y
     no hay fecha en que se desfase 5 %: `diasSinTope` lo dice con `null` en vez
     de con Infinity, que la vista imprimiría como «∞ días». Sin serie plana la
     misma pendiente cero es ruido que se cancela y ahí sí no hay tendencia que
     extrapolar. Una pendiente distinta de cero jamás llega con R² 0/0: la
     compuerta de abajo la sigue gobernando. */
  if (deriva === 0) {
    if (!plana) return null
    return {
      dias: VIGENCIA_TOPE_DIAS,
      diasSinTope: null,
      acotado: true,
      hasta: sumarDias(desde, VIGENCIA_TOPE_DIAS),
    }
  }

  if (r2 < DERIVA_R2_MINIMO) return null

  const mensual = deriva / 100
  if (mensual <= -1) return null // una caída de 100 % o más no es una deriva

  /* Interés compuesto en los dos sentidos: el objetivo es +5 % si el precio
     sube y −5 % si baja. Usar |deriva| contra +5 % daba la respuesta correcta
     por accidente sólo cuando la deriva era chica. */
  const objetivo = mensual > 0 ? 1 + VIGENCIA_DESFASE : 1 - VIGENCIA_DESFASE
  const meses = Math.log(objetivo) / Math.log(1 + mensual)
  if (!Number.isFinite(meses) || meses < 0) return null

  const crudo = Math.round(meses * DAYS_PER_MONTH)
  const dias = Math.min(crudo, VIGENCIA_TOPE_DIAS)
  return {
    dias,
    diasSinTope: crudo,
    acotado: crudo > VIGENCIA_TOPE_DIAS,
    hasta: sumarDias(desde, dias),
  }
}

/**
 * Suma días a una fecha ISO.
 *
 * Ancla el reloj al mediodía UTC por la misma razón que `formatDate`
 * (`format.ts:187`): a medianoche, cualquier huso al oeste de Greenwich mueve
 * la fecha un día para atrás, y esta cifra se imprime en un acta.
 */
function sumarDias(iso: string, dias: number): string {
  const base = Date.parse(`${iso.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(base)) return iso
  const d = new Date(base + dias * 86_400_000)
  const mes = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dia = String(d.getUTCDate()).padStart(2, '0')
  return `${d.getUTCFullYear()}-${mes}-${dia}`
}

/**
 * Hoy, en la zona horaria del lector, como ISO `YYYY-MM-DD`.
 *
 * `new Date().toISOString()` toma la fecha en UTC, y en México (UTC−6/−5) desde
 * las 18:00 locales el ISO ya es el día siguiente: a las 19:30 del 2 de agosto
 * en Monterrey la ficha se emitía «el 3 de agosto de 2026» y la vigencia salía
 * corrida un día, en el documento que se anexa a un acta. Se compone con los
 * getters locales, que es el mismo cuidado con el que `sumarDias` ancla al
 * mediodía UTC para no perder un día por el otro lado.
 */
export function hoyIsoLocal(ahora: Date = new Date()): string {
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')
  return `${ahora.getFullYear()}-${mes}-${dia}`
}

/**
 * Días de calendario de `desde` a `hasta`. Negativo si `hasta` ya pasó.
 *
 * `null` si alguna de las dos no es una fecha legible: la ausencia se declara en
 * prosa, no se rellena con un cero que diría «hoy». Las dos puntas se anclan al
 * mediodía UTC por la misma razón que `sumarDias`.
 */
export function diasEntreIso(desde: string, hasta: string): number | null {
  const a = Date.parse(`${desde.slice(0, 10)}T12:00:00Z`)
  const b = Date.parse(`${hasta.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(a) || Number.isNaN(b)) return null
  return Math.round((b - a) / 86_400_000)
}

/**
 * Deriva acumulada, en por ciento, después de `dias` al ritmo `derivaMensualPct`.
 *
 * Es `vigenciaHasta` leída al revés —el mismo interés compuesto sobre la misma
 * recta— y sirve para lo único que se puede decir cuando la ventana de vigencia
 * ya se agotó: cuánto se movió el precio base desde que se levantó. Con la papa
 * (+20.6 %/mes) y los 65 días de rezago del 2026-08-02 son +49 %.
 */
export function derivaAcumuladaPct(
  derivaMensualPct: number | null,
  dias: number | null,
): number | null {
  if (derivaMensualPct === null || dias === null) return null
  if (!Number.isFinite(derivaMensualPct) || !Number.isFinite(dias)) return null
  const mensual = derivaMensualPct / 100
  if (mensual <= -1) return null // una caída de 100 % o más no es una deriva
  const factor = Math.pow(1 + mensual, dias / DAYS_PER_MONTH)
  if (!Number.isFinite(factor)) return null
  return (factor - 1) * 100
}

/* --- Marcas sobre la banda (§4.3) ---------------------------------------- */

export interface BandMarker {
  value: number
  label: string
  tone: 'anaquel' | 'referencia'
}

/** Rótulo de la marca del INEGI. Dice «otro método» porque es otro método. */
export const INEGI_MARKER_LABEL = 'INEGI (otro método)'

/**
 * Las marcas que van sobre la banda: el precio de anaquel y el del INEGI.
 *
 * Las dos son comparaciones entre series distintas y las dos exigen el MISMO
 * mes, que es el error que este repo ya cometió cuatro veces (`gapVsNational`
 * arriba, la despensa, y las dos de `Panorama`).
 *
 * · **Anaquel.** Sólo cuando el veredicto del pipeline trae número —los tres
 *   primeros de `MarginVerdict`—. En 39 de las 60 referencias el veredicto es
 *   «sin paridad» o «sin pareo», y ahí la mediana de anaquel existe pero no
 *   significa lo que un caret junto a la central haría creer: en el frijol a
 *   granel el autoservicio no vende el bien, y en el queso vende otro.
 * · **INEGI.** Punto exacto del mes de la banda. Nunca `valueAtOrLast`: la
 *   serie del boletín llega a junio de 2026 y las bandas van en mayo, así que
 *   el respaldo al último punto pondría la tortilla de junio ($24.00) donde va
 *   la de mayo ($23.95), bajo un rótulo que dice mayo.
 *
 * El diferencial que estas marcas dejan ver es aritmética sobre dos precios
 * observados, no una estimación: por eso no se marca nada como `derived`, por
 * el mismo razonamiento que `rebased` más arriba.
 */
export function retailMarkers(
  band: PriceBand | null,
  inegi: SeriesPoint[] | null,
): BandMarker[] {
  if (band === null || band.central === null) return []
  const out: BandMarker[] = []

  const conNumero =
    band.margin.verdict === 'anaquel-mas-caro' ||
    band.margin.verdict === 'central-mas-cara' ||
    band.margin.verdict === 'sin-diferencia'

  const anaquel = band.anaquel
  if (conNumero && anaquel !== null && anaquel.period === band.period) {
    out.push({
      value: anaquel.p50,
      label: RETAIL_CHANNEL_LABEL[anaquel.channel],
      tone: 'anaquel',
    })
  }

  const boletin = inegi === null ? undefined : inegi.find((p) => p.period === band.period)
  if (boletin !== undefined) {
    out.push({ value: boletin.value, label: INEGI_MARKER_LABEL, tone: 'referencia' })
  }
  return out
}

/**
 * Desviación de una celda contra su ancla, en pesos y en por ciento.
 *
 * Es el número de §4.6 —«la diferencia es geografía, no calidad»— y por eso
 * exige que las dos celdas sean la MISMA referencia, en el MISMO canal y en el
 * MISMO mes, y que sólo difieran en la geografía. La restricción de canal no es
 * defensiva: sin ella esta función serviría para dividir la mediana nacional de
 * anaquel entre la de central y publicar el cociente como margen, que es
 * exactamente lo que R8 prohíbe (sin parear por entidad el sesgo mediano es de
 * 4.0 pp y llega a 57.7).
 *
 * El resultado NO se marca como derivado: es una resta entre dos precios
 * observados, no una estimación, y marcarla le diría al lector que la cifra es
 * inventada (`rebased`, arriba, con el mismo razonamiento).
 */
export function desviacionGeografica(
  cell: PriceCell | null,
  ancla: PriceCell | null,
): { absoluto: number; pct: number } | null {
  if (cell === null || ancla === null) return null
  if (cell.refId !== ancla.refId || cell.channel !== ancla.channel) return null
  if (cell.period !== ancla.period) return null
  if (ancla.p50 <= 0) return null
  return {
    absoluto: cell.p50 - ancla.p50,
    pct: (cell.p50 / ancla.p50 - 1) * 100,
  }
}

/* --- Ranking de cadenas (§R9, §5.2) -------------------------------------- */

/** Entidades comunes mínimas para publicar un orden entre cadenas. */
export const MIN_ENTIDADES_RANKING = 5

export interface ChainOverlap {
  /** Claves de entidad donde TODAS las cadenas del ranking cotizan. */
  estados: string[]
  total: number
  minimo: number
  /** `false` ⇒ la vista pinta §5.2 en lugar del ranking. */
  suficiente: boolean
}

/**
 * El traslape del ranking, y si alcanza para publicarlo.
 *
 * `ranking` es nullable porque el pipeline sólo emite 30 rankings para las 60
 * referencias: sin ranking el traslape es cero, no «desconocido», y §5.2 se
 * escribe con ese cero.
 *
 * El mínimo es 5 y el texto de §5.2 lo interpola de `minimo`, no lo teclea. Con
 * menos entidades el orden deja de medir precios y empieza a medir dónde tiene
 * tiendas cada cadena: Chedraui cotiza en 22 entidades y Walmart en 29, y el
 * traslape de las cuatro grandes son 19.
 */
export function chainRankingCommonStates(
  ranking: RetailChainRanking | null | undefined,
): ChainOverlap {
  const estados = ranking?.commonStates ?? []
  return {
    estados,
    total: estados.length,
    minimo: MIN_ENTIDADES_RANKING,
    suficiente: estados.length >= MIN_ENTIDADES_RANKING,
  }
}

/* --- Orden de las filas de precio ---------------------------------------- */

export interface RetailRow {
  id: string
  label: string
  sublabel?: string
  /** Lo que se imprime en la columna de valor: pesos por kilo o por litro. */
  value: number
  /** Lo que dibuja la barra. `null` = sin dato, y `BarList` lo pinta como tal. */
  magnitude: number | null
  /**
   * Fija la fila arriba de todo, fuera del orden.
   *
   * La central de abasto es el ancla contra la que se leen las cadenas (§4.7) y
   * no es una competidora más: en la tortilla cotiza $26.00 contra $13.00 de
   * Chedraui, así que cualquier orden por precio la manda al fondo de la lista
   * y la referencia desaparece de la vista.
   */
  ancla?: boolean
}

export type RetailSort = 'barato' | 'caro' | 'nombre'

/**
 * Ordena filas de precio dejando SIEMPRE al final las que no tienen magnitud.
 *
 * Es la misma regla de `sortProductChanges` y `sortPlaceChanges`, y por la
 * misma razón: un vacío no es un mínimo ni un máximo. También aplica en el
 * orden alfabético —una entidad sin barra encabezando la tabla por llamarse
 * Aguascalientes le da la posición de «la más barata» a la que no tiene dato—.
 * El nombre desempata dentro de cada grupo, nunca entre grupos.
 */
export function sortRetailRows(rows: RetailRow[], sort: RetailSort = 'barato'): RetailRow[] {
  const collator = new Intl.Collator('es-MX', { sensitivity: 'base' })

  return [...rows].sort((a, b) => {
    if (a.ancla === true && b.ancla !== true) return -1
    if (b.ancla === true && a.ancla !== true) return 1

    const ka = a.magnitude
    const kb = b.magnitude
    if (ka === null && kb !== null) return 1
    if (ka !== null && kb === null) return -1
    if (sort === 'nombre' || ka === null || kb === null) {
      return collator.compare(a.label, b.label)
    }
    if (ka === kb) return collator.compare(a.label, b.label)
    return sort === 'caro' ? kb - ka : ka - kb
  })
}
